import React from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  styled,
  Slide
} from '@mui/material';
import { useFormik } from 'formik';
import { addressSchema } from '../../utils/validationSchema';
import { LocationOn } from '@mui/icons-material';

interface AddressFormProps {
  onSubmit: (values: { 
    address: string; 
    hasNoFixedAddress: boolean; 
  }) => void;
  onBack: () => void;
  initialValues: {
    address: string;
    hasNoFixedAddress: boolean;
  };
}

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.light,
      },
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: 2,
      },
    },
  },
  '& .MuiInputLabel-root': {
    transition: 'all 0.3s ease',
  },
}));

const AddressForm: React.FC<AddressFormProps> = ({ onSubmit, onBack, initialValues }) => {
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: addressSchema,
    onSubmit: (values) => {
      // Simplified submission without coordinates
      const submitData = {
        address: values.address,
        hasNoFixedAddress: values.hasNoFixedAddress,
        latitude: 0, // Default values
        longitude: 0
      };
      onSubmit(submitData);
    }
  });

  return (
    <Slide direction="up" in={true} mountOnEnter unmountOnExit>
      <Box component="form" onSubmit={formik.handleSubmit} sx={{ maxWidth: 500, mx: 'auto', mt: 2 }}>
        <Typography variant="h5" component="h2" align="center" gutterBottom>
          Địa chỉ doanh nghiệp
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Cung cấp địa chỉ của doanh nghiệp của bạn
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              id="hasNoFixedAddress"
              name="hasNoFixedAddress"
              checked={formik.values.hasNoFixedAddress}
              onChange={formik.handleChange}
              color="primary"
            />
          }
          label="Tôi không có địa chỉ cố định"
          sx={{ mb: 2, display: 'block' }}
        />

        {!formik.values.hasNoFixedAddress && (
          <StyledTextField
            fullWidth
            id="address"
            name="address"
            label="Địa chỉ doanh nghiệp"
            value={formik.values.address}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address && Boolean(formik.errors.address)}
            helperText={formik.touched.address && formik.errors.address}
            multiline
            rows={3}
            placeholder="Nhập địa chỉ đầy đủ của doanh nghiệp"
            InputProps={{
              startAdornment: (
                <LocationOn color="action" sx={{ mr: 1, mt: 2 }} />
              ),
            }}
          />
        )}

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            size="large"
            onClick={onBack}
            sx={{ 
              py: 1.5, 
              borderRadius: 2,
              transition: 'all 0.3s ease',
            }}
          >
            Quay lại
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            sx={{ 
              py: 1.5, 
              borderRadius: 2,
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
              }
            }}
          >
            Tiếp tục
          </Button>
        </Box>
      </Box>
    </Slide>
  );
};

export default AddressForm; 